import { useEffect, useRef, useState } from 'react';

function usePersistentState<T>(
  key: string,
  deserialize: (s: string | null) => T,
  serialize: (t: T) => string,
  defaultValue: T,
) {
  const [val, setVal] = useState<T>(defaultValue);
  const isInit = useRef(false);
  useEffect(() => {
    const storedVal = window.localStorage.getItem(key);
    if (!isInit.current) {
      setVal(deserialize(storedVal) || defaultValue);
      isInit.current = true;
      return;
    }
    const serVal = serialize(val);
    if (serVal !== storedVal) {
      window.localStorage.setItem(key, serVal);
    }
  }, [val]);
  return [val, setVal] as const;
}

export function usePersistentStringState<T extends string = string>(key: string, defaultValue: T) {
  return usePersistentState(
    key,
    (s) => s as T,
    (t) => t,
    defaultValue,
  );
}

export function usePersistentBooleanState(key: string, defaultValue: boolean) {
  return usePersistentState(
    key,
    (s) => s === 'true',
    (t) => t.toString(),
    defaultValue,
  );
}

export function usePersistentObjectState<T extends Object | null>(key: string, defaultValue: T) {
  return usePersistentState(
    key,
    (s) => (!s ? null : (JSON.parse(s || '') as T)),
    (t) => JSON.stringify(t),
    defaultValue,
  );
}
